import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

let _client: SupabaseClient | null = null;
function getClient(): SupabaseClient {
  if (!_client) {
    if (!supabaseUrl || !supabaseAnonKey) {
      // Return a dummy proxy that won't crash but logs warnings
      console.warn('[Supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY — Supabase disabled');
      return new Proxy({} as SupabaseClient, {
        get: (_t, prop) => {
          if (prop === 'auth') return new Proxy({} as any, {
            get: () => async () => ({ data: { user: null, session: null }, error: new Error('Supabase not configured') }),
          });
          if (prop === 'from') return () => new Proxy({} as any, {
            get: () => (..._args: any[]) => Promise.resolve({ data: null, error: new Error('Supabase not configured') }),
          });
          return () => {};
        },
      });
    }
    _client = createClient(supabaseUrl, supabaseAnonKey);
  }
  return _client;
}

export const supabase = getClient();

// ─── Types ───────────────────────────────────────────────────────────
export interface Profile {
  id: string;
  username: string;
  created_at: string;
}

export interface LeaderboardEntry {
  id: string;
  user_id: string;
  username: string;
  kills: number;
  floor_reached: number;
  time_survived: number;
  character_id: string;
  score: number;
  created_at: string;
}

// ─── Auth Helpers ────────────────────────────────────────────────────
export async function signUp(email: string, password: string, username: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { username } }, // store username in user metadata for later
  });
  if (error) throw error;
  return data;
}

export async function submitBugReport(report: {
  reporter: string;
  category: string;
  title: string;
  description: string;
  screen: string;
  session_data?: Record<string, unknown> | null;
}): Promise<void> {
  // Strip session_data — column not yet in schema. Add later via Supabase dashboard.
  const { session_data: _sd, ...coreReport } = report;
  const { error } = await supabase.from('bug_reports').insert(coreReport);
  if (error) throw error;
}

export async function saveGameSession(session: {
  username: string;
  character_id: string;
  character_name: string;
  floor_reached: number;
  kills: number;
  time_survived: number;
  outcome: string;
  events: unknown[];
  final_snapshot: unknown;
}): Promise<void> {
  const { error } = await supabase.from('game_sessions').insert(session);
  if (error) console.error('[Supabase] Session save error:', error);
}

export async function joinWaitlist(email: string, peanutName?: string): Promise<void> {
  const { error } = await supabase
    .from('waitlist')
    .insert({ email: email.toLowerCase().trim(), name: peanutName || null });
  if (error) {
    if (error.code === '23505') throw new Error("That email's already on the list!");
    throw error;
  }
}

/** Called after email confirmation — creates the profile if it doesn't exist yet */
export async function ensureProfile(userId: string, username: string): Promise<void> {
  const existing = await getProfile(userId);
  if (existing) return;
  await supabase.from('profiles').insert({ id: userId, username });
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  // Create profile on first login if it doesn't exist yet (post email confirmation)
  if (data.user) {
    const username = data.user.user_metadata?.username ?? email.split('@')[0];
    await ensureProfile(data.user.id, username);
  }
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) return null;
  return data;
}

// ─── Leaderboard Helpers ─────────────────────────────────────────────
export async function submitScore(entry: {
  kills: number;
  floor_reached: number;
  time_survived: number;
  character_id: string;
  username?: string;   // BETA 21.20 — dev-session (tester-code) fallback name
}) {
  // BETA 21.20 — tester-code / dev-session users have NO Supabase auth user, so the
  // old `if (!user) return` silently dropped every tester score (LogiCC never ranked).
  // Now: real accounts keep user_id + profile name; dev-session users post with
  // user_id null + their dev username. (Requires the leaderboard dashboard change —
  // nullable user_id + an anon-insert RLS policy — or the anon insert is rejected.)
  const { data: { user } } = await supabase.auth.getUser();
  const profile = user ? await getProfile(user.id) : null;
  const name = profile?.username ?? entry.username;
  if (!user && !name) return;   // truly anonymous (no account + no dev name) — skip

  // BETA 21.19 — survival-DEPTH score (Dylan: "highest score wins"; beating the
  // game = god mode now, no 'won'). Floor is the PRIMARY axis so reaching the boss
  // / god mode tops the board (floor 3 = 15M, beats any old kill-farm score), then
  // survival TIME (how long you lasted in the storm), then kills as a tiebreaker.
  // Was kills*100 + floor*1000 + time = a pure AFK kill-farm board.
  const score = entry.floor_reached * 5000000 + Math.floor(entry.time_survived) * 100 + entry.kills * 5;

  const { error } = await supabase.from('leaderboard').insert({
    user_id: user?.id ?? null,
    username: name,
    kills: entry.kills,
    floor_reached: entry.floor_reached,
    time_survived: entry.time_survived,
    character_id: entry.character_id,
    score,
  });
  if (error) console.error('[Supabase] Score submit error:', error);
}

export async function getTopScores(limit = 20): Promise<LeaderboardEntry[]> {
  const { data, error } = await supabase
    .from('leaderboard')
    .select('*')
    .order('score', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data ?? [];
}

export async function getMyBestScore(): Promise<LeaderboardEntry | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('leaderboard')
    .select('*')
    .eq('user_id', user.id)
    .order('score', { ascending: false })
    .limit(1)
    .single();
  if (error) return null;
  return data;
}
